// src/components/admin/StripeAudit.js
import React, { useState, useEffect } from 'react';
import {
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ExternalLink,
  DollarSign,
  CreditCard,
  Users,
  TrendingUp
} from 'lucide-react';
import StripeAdminService from '../../services/StripeAdminService';
import TimeRangeSelector from './TimeRangeSelector';

const StripeAudit = ({ isMobile }) => {
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    label: 'Last 30 Days'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    performAudit();
  }, [selectedTimeRange]);

  const performAudit = async () => {
    setLoading(true);
    try {
      console.log('🔍 Performing Stripe audit for:', selectedTimeRange);
      const audit = await StripeAdminService.performStripeAudit(
        selectedTimeRange.startDate,
        selectedTimeRange.endDate
      );
      setAuditData(audit);
      console.log('✅ Audit complete:', audit);
    } catch (error) {
      console.error('Error performing audit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (range) => {
    setSelectedTimeRange(range);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
      case 'requires_payment_method':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'canceled':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'failed':
      case 'requires_payment_method':
        return 'bg-red-100 text-red-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const filteredPayments = auditData?.payments?.filter(payment => {
    const matchesSearch = searchTerm === '' ||
      payment.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.id.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;

    return matchesSearch && matchesStatus;
  }) || [];

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-[#3A98B9] mx-auto mb-4" />
            <p className="text-gray-600">Auditing Stripe data...</p>
            <p className="text-sm text-gray-500 mt-1">This may take a moment</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">🔍 Stripe Audit & Diagnostics</h2>
        <p className="text-gray-600 mb-4">
          Find all Stripe payments, refunds, and identify missing connections with bookings
        </p>

        {/* Time Range Selector and Refresh */}
        <div className="flex items-center space-x-4 mb-6">
          <TimeRangeSelector
            onRangeChange={handleTimeRangeChange}
            initialRange="last30days"
          />
          <button
            onClick={performAudit}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-[#3A98B9] text-white rounded-lg hover:bg-[#2A87A8] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Audit
          </button>
        </div>
      </div>

      {auditData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Payments</p>
                  <p className="text-2xl font-bold">{auditData.summary.totals?.paymentIntents || 0}</p>
                  <p className="text-blue-200 text-xs mt-1">
                    {auditData.summary.statusBreakdown?.succeeded || 0} succeeded
                  </p>
                </div>
                <CreditCard className="h-8 w-8 text-blue-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Total Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(auditData.summary.amounts?.totalGross || 0)}</p>
                  <p className="text-green-200 text-xs mt-1">
                    Net: {formatCurrency(auditData.summary.amounts?.totalNet || 0)}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-green-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">Refunds</p>
                  <p className="text-2xl font-bold">{formatCurrency(auditData.summary.amounts?.totalRefunded || 0)}</p>
                  <p className="text-red-200 text-xs mt-1">
                    {auditData.summary.totals?.refunds || 0} transactions
                  </p>
                </div>
                <RefreshCw className="h-8 w-8 text-red-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Issues Found</p>
                  <p className="text-2xl font-bold">{auditData.summary.issues?.orphanedPayments || 0}</p>
                  <p className="text-orange-200 text-xs mt-1">
                    orphaned payments
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-200" />
              </div>
            </div>
          </div>

          {/* Issues Alert */}
          {(auditData.summary.issues?.orphanedPayments || 0) > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Issues Detected</h3>
                  <div className="mt-1 text-sm text-yellow-700">
                    <p>• {auditData.summary.issues?.orphanedPayments || 0} payments without corresponding bookings</p>
                    <p>• {auditData.summary.issues?.paymentsWithRefunds || 0} payments have been refunded</p>
                    {(auditData.summary.issues?.testPayments || 0) > 0 && (
                      <p>• {auditData.summary.issues?.testPayments || 0} test payments detected</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by email, description, or payment ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-[#3A98B9] focus:border-[#3A98B9]"
              />
            </div>

            <div className="flex items-center space-x-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#3A98B9] focus:border-[#3A98B9]"
              >
                <option value="all">All Status</option>
                <option value="succeeded">Succeeded</option>
                <option value="requires_payment_method">Failed</option>
                <option value="canceled">Canceled</option>
              </select>

              <span className="text-sm text-gray-600">
                {filteredPayments.length} of {auditData.payments?.length || 0} payments
              </span>
            </div>
          </div>

          {/* Payments List */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">All Stripe Payments</h3>
            </div>

            <div className="divide-y divide-gray-200">
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment) => (
                  <div key={payment.id} className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getStatusIcon(payment.status)}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                            {payment.status}
                          </span>
                          <span className="text-sm font-mono text-gray-600">{payment.id}</span>
                          {payment.isOrphaned && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              ⚠️ No Booking Found
                            </span>
                          )}
                          {payment.hasRefunds && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              🔄 Refunded
                            </span>
                          )}
                          {payment.isTestPayment && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              🧪 Test
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Customer:</span>
                            <div className="font-medium">
                              {payment.customerName || 'No name'}
                            </div>
                            <div className="text-gray-600">
                              {payment.customerEmail || 'No email'}
                            </div>
                          </div>

                          <div>
                            <span className="text-gray-500">Amount:</span>
                            <div className="font-medium text-lg">
                              {formatCurrency(payment.amount)}
                            </div>
                            {payment.hasRefunds && (
                              <div className="text-red-600 text-xs">
                                Refunded: {formatCurrency(payment.totalRefunded)}
                              </div>
                            )}
                          </div>

                          <div>
                            <span className="text-gray-500">Payment Method:</span>
                            <div className="font-medium">
                              {payment.cardBrand?.toUpperCase()} ••••{payment.cardLast4}
                            </div>
                            <div className="text-gray-600 capitalize">
                              {payment.paymentMethod}
                            </div>
                          </div>

                          <div>
                            <span className="text-gray-500">Date:</span>
                            <div className="font-medium">
                              {formatDate(payment.created)}
                            </div>
                          </div>
                        </div>

                        {payment.description && (
                          <div className="mt-2 text-sm">
                            <span className="text-gray-500">Description:</span>
                            <span className="ml-2 text-gray-700">{payment.description}</span>
                          </div>
                        )}

                        {payment.refunds.length > 0 && (
                          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                            <h4 className="text-sm font-medium text-red-800 mb-2">Refunds:</h4>
                            {payment.refunds.map((refund) => (
                              <div key={refund.id} className="flex justify-between items-center text-xs text-red-700">
                                <span>{formatDate(refund.created)} - {refund.reason || 'No reason'}</span>
                                <span className="font-medium">{formatCurrency(refund.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex-shrink-0 ml-4">
                        {payment.receiptUrl && (
                          <a
                            href={payment.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Receipt
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  No payments found for the selected criteria
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StripeAudit;